import { createFocusTrap } from 'focus-trap'
import { module as Module } from 'modujs'
import { $html } from '../utils/dom'

/**
 * Generic component to display a modal.
 *
 */
export default class extends Module {
    /**
     * Creates a new Modal.
     *
     * @param  {object} options          - The module options.
     * @param  {string} options.name     - The module class name.
     * @param  {string} options.dataName - The module data attribute name.
     * @param  {string} options.targets   - The targets of the focusTrap.
     * @throws {TypeError} If the class does not have an active CSS class defined.
     */
    constructor(options) {
        super(options) 

        this.moduleName = options.name

        this.events = {
            click: {
                close: 'close',
            },
        }

        this.closeBind = (e) => {
            if (e.key === 'Escape') {
                this.close()
            }
        }
        this.toggleBind = this.toggle.bind(this)

        this.$togglers = []

        this.focusTrapOptions = {
            /**
             * There is a delay between when the class is applied
             * and when the element is focusable
             */
            checkCanFocusTrap: (trapContainers) => {
                const results = trapContainers.map((trapContainer) => {
                    return new Promise((resolve) => {
                        const interval = setInterval(() => {
                            if (
                                getComputedStyle(trapContainer).visibility !==
                                'hidden'
                            ) {
                                resolve()
                                clearInterval(interval)
                            }
                        }, 5)
                    })
                })

                // Return a promise that resolves when all the trap containers are able to receive focus
                return Promise.all(results)
            },

            onActivate: () => {
                this.el.classList.add('is-active')
                $html.classList.add('has-modal-open')
                $html.classList.add('has-'+this.moduleName.toLowerCase()+'-open')
                this.el.setAttribute('aria-hidden', false)

                this.onActivate?.();
            },

            onPostActivate: () => {
                if (this.$togglers) {
                    for (const $toggler of this.$togglers) {
                        $toggler.setAttribute('aria-expanded', true)
                    }
                }
            },

            onDeactivate: () => {
                this.el.classList.remove('is-active')
                $html.classList.remove('has-modal-open')
                $html.classList.remove('has-'+this.moduleName.toLowerCase()+'-open')
                this.el.setAttribute('aria-hidden', true)

                this.onDeactivate?.();
            },

            onPostDeactivate: () => {
                if (this.$togglers) {
                    for (const $toggler of this.$togglers) {
                        $toggler.setAttribute('aria-expanded', false)
                    }
                }
            },
        }

        this.isOpen = false

        // Store site modals
        if (!window.modals) {
            window.modals = []
        }
        window.modals.push(this.moduleName)
    }

     /**
     * @param  {string} targets  - The targets of the focusTrap.
     */

    init(targets) {
        this.onBeforeInit?.()
        this.focusTrap = createFocusTrap(
            targets ?? [this.el],
            this.focusTrapOptions
        )

        document.addEventListener('keyup', this.closeBind)

        this.onInit?.()
    }

    toggle(event) {
        if (this.el.classList.contains('is-active')) {
            this.close(event)
        } else {
            this.open(event)
        }
    }

    open(args) {
        if (this.isOpen) return

        // Close other modals opened
        const modalsToClose = window.modals.filter(
            (modal) => modal != this.moduleName
        )
        for (const modalName of modalsToClose) {
            this.call('close', null, modalName)
        }

        this.isOpen = true

        this.focusTrap?.activate?.()

        this.onOpen?.(args)
    }

    close(args) {
        if (!this.isOpen) return

        this.isOpen = false

        this.focusTrap?.deactivate?.()

        this.onClose?.(args)
    }

    destroy() {
        this.focusTrap?.deactivate?.({
            returnFocus: false,
        })

        document.removeEventListener('keyup', this.closeBind)

        this.onDestroy?.()

        super.destroy()
    }
}
